import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  useTheme,
  Collapse,
  Card,
  CardContent,
  useMediaQuery,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewColumn as ViewColumnIcon,
  MoreVert as MoreVertIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Clear as ClearIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { useResponsive } from '../../hooks/useResponsive';

/**
 * Styled components with Waltero branding
 */
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
  boxShadow: '0px 2px 12px rgba(2, 56, 97, 0.08)',
  overflow: 'hidden',
  
  '& .MuiTable-root': {
    minWidth: 650,
  }
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.waltero.primary.backgroundBlue,
  
  '& .MuiTableCell-head': {
    color: theme.palette.waltero.primary.coreBlue,
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `2px solid ${theme.palette.waltero.primary.neutralBlue}`,
    padding: theme.spacing(1.5, 2),
    
    '&:first-of-type': {
      paddingLeft: theme.spacing(3)
    },
    
    '&:last-of-type': {
      paddingRight: theme.spacing(3)
    }
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease-in-out',
  cursor: 'pointer',
  
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
  },
  
  '&:hover': {
    backgroundColor: theme.palette.waltero.primary.paleBlue,
    transform: 'translateY(-1px)',
    boxShadow: '0px 2px 8px rgba(2, 56, 97, 0.1)',
  },
  
  '&.Mui-selected': {
    backgroundColor: theme.palette.waltero.primary.lightBlue + '20',
    
    '&:hover': {
      backgroundColor: theme.palette.waltero.primary.lightBlue + '30',
    }
  },
  
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
    padding: theme.spacing(1.5, 2),
    
    '&:first-of-type': {
      paddingLeft: theme.spacing(3)
    },
    
    '&:last-of-type': {
      paddingRight: theme.spacing(3)
    }
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.waltero.primary.mediumBlue,
  fontSize: '0.875rem',
  
  '&.numeric': {
    fontFamily: 'Monaco, Consolas, "Roboto Mono", monospace',
    textAlign: 'right'
  },
  
  '&.status': {
    '& .MuiChip-root': {
      fontSize: '0.75rem',
      height: '24px'
    }
  }
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.waltero.primary.backgroundBlue,
  borderBottom: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
  flexWrap: 'wrap',
  
  '& .search-field': {
    minWidth: '300px',
    flex: 1,
    maxWidth: '500px'
  }
}));

/**
 * Mobile Card View Component
 * Displays table row data in card format for mobile devices
 */
const MobileDataCard = ({ 
  row, 
  columns, 
  onRowClick, 
  selected = false,
  expandable = false,
  expanded = false,
  onToggleExpand,
  renderExpandedRow,
  actions = []
}) => {
  const theme = useTheme();
  const responsive = useResponsive();
  
  // Find primary column (usually first visible column)
  const primaryColumn = columns.find(col => !col.hidden) || columns[0];
  const secondaryColumns = columns.filter(col => col !== primaryColumn && !col.hidden);
  
  const handleCardClick = (event) => {
    if (onRowClick) {
      onRowClick(event, row);
    }
  };
  
  const handleExpandClick = (event) => {
    event.stopPropagation();
    if (onToggleExpand) {
      onToggleExpand(row);
    }
  };
  
  return (
    <Card
      sx={{
        mb: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
        backgroundColor: selected 
          ? `${theme.palette.waltero.primary.lightBlue}20`
          : theme.palette.background.paper,
        cursor: onRowClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onRowClick ? {
          backgroundColor: selected 
            ? `${theme.palette.waltero.primary.lightBlue}30`
            : theme.palette.waltero.secondary.backgroundApricot,
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows[4]
        } : {},
        '&:active': {
          transform: 'translateY(0px)',
          boxShadow: theme.shadows[2]
        }
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: responsive.utils.getSpacing(1.5), '&:last-child': { pb: responsive.utils.getSpacing(1.5) } }}>
        {/* Primary content row */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {/* Primary column content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: theme.palette.waltero.primary.deepBlue,
                fontSize: responsive.utils.getFontSize(1),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {primaryColumn.render 
                ? primaryColumn.render(row[primaryColumn.key], row)
                : row[primaryColumn.key]
              }
            </Typography>
            {primaryColumn.subtext && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.waltero.primary.mediumBlue,
                  fontSize: responsive.utils.getFontSize(0.75)
                }}
              >
                {typeof primaryColumn.subtext === 'function' 
                  ? primaryColumn.subtext(row) 
                  : row[primaryColumn.subtext]
                }
              </Typography>
            )}
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {expandable && (
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{
                  minWidth: responsive.touch.minTouchTarget,
                  minHeight: responsive.touch.minTouchTarget,
                  color: theme.palette.waltero.primary.mediumBlue
                }}
              >
                {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            )}
          </Box>
        </Box>
        
        {/* Secondary content - show 2-3 most important columns */}
        {secondaryColumns.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
            {secondaryColumns.slice(0, 3).map((column) => (
              <Box key={column.key}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.waltero.primary.mediumBlue,
                    fontSize: responsive.utils.getFontSize(0.75),
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {column.header}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.waltero.primary.coreBlue,
                    fontSize: responsive.utils.getFontSize(0.875),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {column.render 
                    ? column.render(row[column.key], row)
                    : row[column.key]
                  }
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Expanded content */}
        <Collapse in={expanded}>
          {expanded && renderExpandedRow && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.waltero.primary.neutralBlue}` }}>
              {renderExpandedRow(row)}
            </Box>
          )}
          
          {/* Show remaining columns in expanded state */}
          {expanded && secondaryColumns.length > 3 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.waltero.primary.neutralBlue}` }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.waltero.primary.deepBlue,
                  mb: 1,
                  fontWeight: 600
                }}
              >
                Additional Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                {secondaryColumns.slice(3).map((column) => (
                  <Box key={column.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.waltero.primary.mediumBlue,
                        fontWeight: 500
                      }}
                    >
                      {column.header}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.waltero.primary.coreBlue,
                        textAlign: 'right',
                        maxWidth: '60%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {column.render 
                        ? column.render(row[column.key], row)
                        : row[column.key]
                      }
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

/**
 * Enhanced WalteroDataTable Component
 * 
 * A sophisticated data table with advanced features including:
 * - Sorting and filtering
 * - Column visibility controls
 * - Keyboard navigation
 * - Accessibility compliance (WCAG 2.1 AA)
 * - Context menus and bulk actions
 * - Responsive design
 * - Export capabilities
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.data - Array of data objects
 * @param {Array} props.columns - Column configuration array
 * @param {boolean} [props.loading=false] - Loading state
 * @param {boolean} [props.sortable=true] - Enable sorting
 * @param {boolean} [props.filterable=true] - Enable filtering
 * @param {boolean} [props.selectable=false] - Enable row selection
 * @param {boolean} [props.expandable=false] - Enable row expansion
 * @param {Function} [props.onRowClick] - Row click handler
 * @param {Function} [props.onSelectionChange] - Selection change handler
 * @param {Function} [props.renderExpandedRow] - Render function for expanded content
 * @param {Object} [props.pagination] - Pagination configuration
 * @param {Array} [props.actions] - Table actions
 * @param {string} [props.ariaLabel] - Accessibility label
 * @param {string} [props.emptyMessage='No data available'] - Empty state message
 */
const WalteroDataTable = ({
  data = [],
  columns = [],
  loading = false,
  sortable = true,
  filterable = true,
  selectable = false,
  expandable = false,
  onRowClick,
  onSelectionChange,
  renderExpandedRow,
  pagination,
  actions = [],
  showControls = true,
  rowActions,
  ariaLabel = 'Data table',
  emptyMessage = 'No data available',
  ...otherProps
}) => {
  const theme = useTheme();
  const responsive = useResponsive();
  const tableRef = useRef(null);
  
  // Mobile view state
  const [mobileViewMode, setMobileViewMode] = useState('cards'); // 'cards' or 'table'
  
  // State management
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.defaultPageSize || 25);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col.id]: col.visible !== false }), {})
  );
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Handle row expansion toggle
  const handleToggleExpand = (rowId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };
  
  // Menu states
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [contextMenuRow, setContextMenuRow] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const hasRowActions = typeof rowActions === 'function';

  // Keyboard navigation state
  const [focusedCell, setFocusedCell] = useState({ row: -1, col: -1 });

  // Filter data based on search term and column filters
  const filteredData = useMemo(() => {
    let filtered = data;

    // Global search
    if (searchTerm) {
      filtered = filtered.filter(row =>
        columns.some(col => {
          if (!visibleColumns[col.id]) return false;
          const value = col.accessor ? col.accessor(row) : row[col.id];
          return String(value || '').toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Column-specific filters
    Object.entries(columnFilters).forEach(([colId, filterValue]) => {
      if (filterValue) {
        filtered = filtered.filter(row => {
          const column = columns.find(col => col.id === colId);
          const value = column?.accessor ? column.accessor(row) : row[colId];
          return String(value || '').toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    return filtered;
  }, [data, searchTerm, columnFilters, columns, visibleColumns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortable || !orderBy) return filteredData;

    const column = columns.find(col => col.id === orderBy);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = column.accessor ? column.accessor(a) : a[orderBy];
      const bVal = column.accessor ? column.accessor(b) : b[orderBy];
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return order === 'asc' ? -1 : 1;
      if (bVal == null) return order === 'asc' ? 1 : -1;
      
      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (aStr < bStr) return order === 'asc' ? -1 : 1;
      if (aStr > bStr) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, order, orderBy, columns, sortable]);

  // Paginated data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage, pagination]);

  // Visible columns
  const displayColumns = useMemo(() => 
    columns.filter(col => visibleColumns[col.id]), 
    [columns, visibleColumns]
  );

  // Event handlers
  const handleRequestSort = useCallback((property) => {
    if (!sortable) return;
    
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [order, orderBy, sortable]);

  const handleSelectAllClick = useCallback((event) => {
    if (event.target.checked) {
      const newSelected = paginatedData.map(row => row.id || row);
      setSelected(newSelected);
      onSelectionChange?.(newSelected);
    } else {
      setSelected([]);
      onSelectionChange?.([]);
    }
  }, [paginatedData, onSelectionChange]);

  const handleRowSelect = useCallback((event, id) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = [
        ...selected.slice(0, selectedIndex),
        ...selected.slice(selectedIndex + 1),
      ];
    }

    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  }, [selected, onSelectionChange]);

  const handleRowClick = useCallback((event, row, index) => {
    if (onRowClick) {
      onRowClick(event, row, index);
    }
  }, [onRowClick]);

  const handleExpandRow = useCallback((rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  }, [expandedRows]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
    setFocusedCell({ row: -1, col: -1 });
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value
    }));
  }, []);

  const handleColumnVisibilityChange = useCallback((columnId, visible) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: visible
    }));
  }, []);

  // Context menu handlers
  const handleContextMenu = useCallback((event, row) => {
    event.preventDefault();
    setContextMenuAnchor(event.currentTarget);
    setContextMenuRow(row);
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuAnchor(null);
    setContextMenuRow(null);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!tableRef.current) return;
    
    const { key, ctrlKey, metaKey } = event;
    const { row, col } = focusedCell;
    const maxRow = paginatedData.length - 1;
    const maxCol = displayColumns.length - 1 + (selectable ? 1 : 0) + (expandable ? 1 : 0);
    
    let newRow = row;
    let newCol = col;
    
    switch (key) {
      case 'ArrowUp':
        event.preventDefault();
        newRow = Math.max(0, row - 1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        newRow = Math.min(maxRow, row + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        newCol = Math.max(0, col - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        newCol = Math.min(maxCol, col + 1);
        break;
      case 'Home':
        event.preventDefault();
        if (ctrlKey || metaKey) {
          newRow = 0;
          newCol = 0;
        } else {
          newCol = 0;
        }
        break;
      case 'End':
        event.preventDefault();
        if (ctrlKey || metaKey) {
          newRow = maxRow;
          newCol = maxCol;
        } else {
          newCol = maxCol;
        }
        break;
      case 'Enter':
      case ' ':
        if (row >= 0 && row < paginatedData.length) {
          event.preventDefault();
          const rowData = paginatedData[row];
          if (key === ' ' && selectable) {
            handleRowSelect(event, rowData.id || rowData);
          } else if (key === 'Enter') {
            handleRowClick(event, rowData, row);
          }
        }
        break;
      default:
        return;
    }
    
    setFocusedCell({ row: newRow, col: newCol });
  }, [focusedCell, paginatedData, displayColumns, selectable, expandable, handleRowSelect, handleRowClick]);

  // Focus management
  useEffect(() => {
    if (tableRef.current && focusedCell.row >= 0 && focusedCell.col >= 0) {
      const targetCell = tableRef.current.querySelector(
        `tr[data-row-index="${focusedCell.row}"] td[data-col-index="${focusedCell.col}"]`
      );
      if (targetCell) {
        targetCell.focus();
      }
    }
  }, [focusedCell]);

  // Check if row is selected
  const isSelected = useCallback((id) => selected.indexOf(id) !== -1, [selected]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setColumnFilters({});
  }, []);

  // Check if any filters are active
  const hasActiveFilters = searchTerm || Object.values(columnFilters).some(Boolean);

  if (loading) {
    return (
      <StyledTableContainer>
        <Box display="flex" justifyContent="center" alignItems="center" p={8}>
          <CircularProgress size={48} sx={{ color: theme.palette.waltero.primary.coreBlue }} />
        </Box>
      </StyledTableContainer>
    );
  }

  // Mobile card view render
  const renderMobileCardView = () => (
    <Box sx={{ p: responsive.utils.getSpacing(1) }}>
      {paginatedData.length === 0 ? (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          p={responsive.utils.getSpacing(4)}
          sx={{ textAlign: 'center' }}
        >
          <Typography 
            variant="body1" 
            color="textSecondary"
            sx={{ fontSize: responsive.utils.getFontSize(1) }}
          >
            {hasActiveFilters ? 'No results found for your search.' : emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Box>
          {paginatedData.map((row, index) => {
            const rowId = row.id || index;
            const isItemSelected = isSelected(rowId);
            const isExpanded = expandedRows.has(rowId);
            
            return (
              <MobileDataCard
                key={rowId}
                row={row}
                columns={displayColumns}
                onRowClick={onRowClick ? (event, rowData) => handleRowClick(event, rowData, index) : undefined}
                selected={isItemSelected}
                expandable={expandable}
                expanded={isExpanded}
                onToggleExpand={expandable ? () => handleToggleExpand(rowId) : undefined}
                renderExpandedRow={renderExpandedRow}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );

  return (
    <StyledTableContainer
      ref={tableRef}
      onKeyDown={!responsive.breakpoints.isMobile ? handleKeyDown : undefined}
      tabIndex={!responsive.breakpoints.isMobile ? 0 : undefined}
      role={responsive.breakpoints.isMobile ? undefined : "grid"}
      aria-label={ariaLabel}
      sx={{
        // Mobile optimizations
        ...(responsive.breakpoints.isMobile && {
          '& .MuiTable-root': {
            minWidth: 'unset'
          }
        })
      }}
      {...otherProps}
    >
      {/* Controls */}
      {showControls && (filterable || actions.length > 0) && (
        <ControlsContainer
          sx={{
            // Mobile optimizations
            ...(responsive.breakpoints.isMobile && {
              flexDirection: 'column',
              gap: responsive.utils.getSpacing(1),
              '& .search-field': {
                minWidth: 'unset',
                maxWidth: 'unset',
                width: '100%'
              }
            })
          }}
        >
          {filterable && (
            <TextField
              className="search-field"
              variant="outlined"
              size={responsive.breakpoints.isMobile ? "medium" : "small"}
              placeholder="Search table data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      aria-label="Clear search"
                      sx={{
                        minWidth: responsive.touch.minTouchTarget,
                        minHeight: responsive.touch.minTouchTarget
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              aria-label="Search table data"
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  ...(responsive.breakpoints.isMobile && {
                    minHeight: responsive.touch.minTouchTarget
                  })
                }
              }}
            />
          )}
          
          {filterable && (
            <Tooltip title="Column filters">
              <IconButton
                onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                color={Object.values(columnFilters).some(Boolean) ? 'primary' : 'default'}
                aria-label="Open column filters"
                sx={{
                  minWidth: responsive.touch.minTouchTarget,
                  minHeight: responsive.touch.minTouchTarget
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Column visibility">
            <IconButton
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
              aria-label="Show/hide columns"
              sx={{
                minWidth: responsive.touch.minTouchTarget,
                minHeight: responsive.touch.minTouchTarget
              }}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          
          {hasActiveFilters && (
            <Chip
              label="Clear filters"
              variant="outlined"
              size="small"
              onDelete={clearFilters}
              sx={{ color: theme.palette.waltero.primary.coreBlue }}
            />
          )}
          
          {actions.map((action, index) => {
            if (!action) return null;
            if (typeof action === 'function') {
              return (
                <Box key={index}>
                  {action({ selected })}
                </Box>
              );
            }
            if (action?.element) {
              return (
                <Box key={index}>
                  {action.element}
                </Box>
              );
            }
            return null;
          })}
        </ControlsContainer>
      )}

      {/* Mobile view toggle */}
      {responsive.breakpoints.isMobile && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          p: 1, 
          borderBottom: `1px solid ${theme.palette.waltero.primary.neutralBlue}` 
        }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setMobileViewMode('cards')}
              sx={{
                bgcolor: mobileViewMode === 'cards' ? theme.palette.waltero.secondary.backgroundApricot : 'transparent',
                color: mobileViewMode === 'cards' ? theme.palette.waltero.primary.deepBlue : theme.palette.text.secondary,
                minWidth: responsive.touch.minTouchTarget,
                minHeight: responsive.touch.minTouchTarget
              }}
            >
              <ViewColumnIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setMobileViewMode('table')}
              sx={{
                bgcolor: mobileViewMode === 'table' ? theme.palette.waltero.secondary.backgroundApricot : 'transparent',
                color: mobileViewMode === 'table' ? theme.palette.waltero.primary.deepBlue : theme.palette.text.secondary,
                minWidth: responsive.touch.minTouchTarget,
                minHeight: responsive.touch.minTouchTarget
              }}
            >
              <FilterIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Conditional rendering: Mobile cards or desktop table */}
      {responsive.breakpoints.isMobile && mobileViewMode === 'cards' ? (
        renderMobileCardView()
      ) : (
        <Table aria-label={ariaLabel} size={responsive.table.size}>
        <StyledTableHead>
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={selected.length > 0 && selected.length < paginatedData.length}
                  checked={paginatedData.length > 0 && selected.length === paginatedData.length}
                  onChange={handleSelectAllClick}
                  inputProps={{
                    'aria-label': 'select all rows',
                  }}
                />
              </TableCell>
            )}
            
            {expandable && <TableCell />}
            
            {displayColumns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || (column.type === 'number' ? 'right' : 'left')}
                sortDirection={orderBy === column.id ? order : false}
              >
                {sortable && column.sortable !== false ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => handleRequestSort(column.id)}
                    sx={{
                      color: theme.palette.waltero.primary.coreBlue,
                      '&.Mui-active': {
                        color: theme.palette.waltero.secondary.primaryApricot
                      }
                    }}
                  >
                    {column.label}
                    {orderBy === column.id && (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    )}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </StyledTableHead>
        
        <TableBody>
          {paginatedData.length === 0 ? (
            <TableRow>
              <StyledTableCell 
                colSpan={displayColumns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0)}
                align="center"
              >
                <Box py={4}>
                  <Typography variant="body1" color="textSecondary">
                    {emptyMessage}
                  </Typography>
                </Box>
              </StyledTableCell>
            </TableRow>
          ) : (
            paginatedData.map((row, index) => {
              const rowId = row.id || index;
              const isItemSelected = isSelected(rowId);
              const isExpanded = expandedRows.has(rowId);
              
              return (
                <React.Fragment key={rowId}>
                  <StyledTableRow
                    hover
                    onClick={(event) => handleRowClick(event, row, index)}
                    onContextMenu={(event) => handleContextMenu(event, row)}
                    role="row"
                    aria-checked={selectable ? isItemSelected : undefined}
                    tabIndex={-1}
                    selected={isItemSelected}
                    data-row-index={index}
                  >
                    {selectable && (
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(event) => handleRowSelect(event, rowId)}
                          inputProps={{
                            'aria-labelledby': `enhanced-table-checkbox-${index}`,
                          }}
                        />
                      </StyledTableCell>
                    )}
                    
                    {expandable && (
                      <StyledTableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandRow(rowId);
                          }}
                          aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        >
                          {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </StyledTableCell>
                    )}
                    
                    {displayColumns.map((column, colIndex) => {
                      const value = column.accessor ? column.accessor(row) : row[column.id];
                      
                      return (
                        <StyledTableCell
                          key={column.id}
                          align={column.align || (column.type === 'number' ? 'right' : 'left')}
                          className={column.type === 'number' ? 'numeric' : ''}
                          data-col-index={colIndex + (selectable ? 1 : 0) + (expandable ? 1 : 0)}
                          tabIndex={-1}
                        >
                          {column.render ? column.render(value, row, index) : value}
                        </StyledTableCell>
                      );
                    })}
                  </StyledTableRow>
                  
                  {expandable && isExpanded && renderExpandedRow && (
                    <TableRow>
                      <StyledTableCell 
                        colSpan={displayColumns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0)}
                        sx={{ py: 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            {renderExpandedRow(row, index)}
                          </Box>
                        </Collapse>
                      </StyledTableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
      )}

      {/* Pagination */}
      {pagination && (
        <TablePagination
          rowsPerPageOptions={responsive.table.pagination.rowsPerPageOptions}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton={responsive.table.pagination.showFirstButton}
          showLastButton={responsive.table.pagination.showLastButton}
          sx={{
            backgroundColor: theme.palette.waltero.primary.backgroundBlue,
            borderTop: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
            color: theme.palette.waltero.primary.coreBlue,
            '& .MuiTablePagination-select': {
              color: theme.palette.waltero.primary.coreBlue
            },
            // Mobile optimizations
            ...(responsive.breakpoints.isMobile && {
              '& .MuiTablePagination-toolbar': {
                minHeight: responsive.touch.minTouchTarget,
                paddingLeft: responsive.utils.getSpacing(1),
                paddingRight: responsive.utils.getSpacing(1)
              },
              '& .MuiIconButton-root': {
                minWidth: responsive.touch.minTouchTarget,
                minHeight: responsive.touch.minTouchTarget
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: responsive.utils.getFontSize(0.875)
              }
            })
          }}
        />
      )}

      {/* Column Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        PaperProps={{
          sx: { minWidth: 300, maxHeight: 400 }
        }}
      >
        <Box p={2}>
          <Typography variant="subtitle2" gutterBottom>
            Column Filters
          </Typography>
          {displayColumns.map((column) => (
            <Box key={column.id} mb={2}>
              <TextField
                fullWidth
                size="small"
                label={column.label}
                value={columnFilters[column.id] || ''}
                onChange={(e) => handleColumnFilterChange(column.id, e.target.value)}
                variant="outlined"
              />
            </Box>
          ))}
        </Box>
      </Menu>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
        PaperProps={{
          sx: { minWidth: 250, maxHeight: 400 }
        }}
      >
        <Box p={2}>
          <Typography variant="subtitle2" gutterBottom>
            Show/Hide Columns
          </Typography>
          {columns.map((column) => (
            <FormControlLabel
              key={column.id}
              control={
                <Checkbox
                  checked={visibleColumns[column.id]}
                  onChange={(e) => handleColumnVisibilityChange(column.id, e.target.checked)}
                  size="small"
                />
              }
              label={column.label}
            />
          ))}
        </Box>
      </Menu>

      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleCloseContextMenu}>View Details</MenuItem>
        <MenuItem onClick={handleCloseContextMenu}>Edit</MenuItem>
        <MenuItem onClick={handleCloseContextMenu}>Export Row</MenuItem>
      </Menu>
    </StyledTableContainer>
  );
};

export default WalteroDataTable;
